import React from 'react';
import { useForm, UseFormHandleSubmit, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TaskSchema, taskSchema } from '../schemas';
import { useTaskOptions } from '../hooks';
import { HttpMethod } from '../enums';
import {
  FormCronInput,
  FormSelectInput,
  FormTextAreaInput,
  FormTextInput,
  FormToggleSwitch,
} from '@/components';

interface TaskFormProps {
  defaultValues: TaskSchema;
  action: (data: TaskSchema, reset: () => void) => void;
  renderButtons: (
    handleSubmit: UseFormHandleSubmit<TaskSchema>,
    reset: () => void
  ) => React.ReactNode;
}

const TaskForm = ({ defaultValues, renderButtons, action }: TaskFormProps) => {
  const { handleSubmit, control, reset } = useForm<TaskSchema>({
    resolver: zodResolver(taskSchema),
    defaultValues,
  });

  const onSubmit = (data: TaskSchema) => action(data, reset);

  const { methodsOptions } = useTaskOptions();

  const method = useWatch({ control, name: 'method' });
  const isGet = method === HttpMethod.GET;

  return (
    <form
      className='flex flex-col gap-5 bg-white p-12 shadow-sm mx-auto w-[920px]'
      onSubmit={handleSubmit(onSubmit)}
    >
      <h2 className='text-2xl !font-bold select-none'>Crear tarea</h2>
      <div className='flex gap-5'>
        <FormTextInput
          name='name'
          placeholder='Nombre de la tarea*'
          control={control}
          autoFocus
        />
      </div>
      <div className='flex gap-5'>
        <div className='min-w-[180px]'>
          <FormSelectInput
            name='method'
            control={control}
            options={methodsOptions}
            placeholder='Selecciona un método'
          />
        </div>
        <FormTextInput name='url' placeholder='URL' control={control} />
      </div>
      <div className='flex items-center gap-8'>
        <p>Necesita autentificación</p>
        <FormToggleSwitch control={control} name='authRequired' />
      </div>
      <div className='flex flex-col gap-2'>
        <p className='!font-bold'>
          {isGet ? 'Parámetros de query (JSON)' : 'Body (JSON)'}
        </p>
        <p className='text-sm text-gray-500'>
          {isGet
            ? 'Opcional. Se envía como query string, por ej. {"companyId": "123"}.'
            : 'Opcional. Se envía como body de la request, por ej. {"companyId": "123"}.'}
        </p>
        <FormTextAreaInput
          name='bodyText'
          placeholder={isGet ? 'Parámetros' : 'Body'}
          control={control}
          rows={6}
        />
      </div>
      <div className='flex flex-col gap-2'>
        <p className='!font-bold'>Headers (JSON)</p>
        <p className='text-sm text-gray-500'>
          Opcional. Se suman a los headers por defecto, por ej.
          {' {"X-Api-Key": "abc"}'}.
        </p>
        <FormTextAreaInput
          name='headersText'
          placeholder='Headers'
          control={control}
          rows={4}
        />
      </div>
      <p className='!font-bold'>Expresión CRON</p>
      <FormCronInput name='cronExpression' control={control} />
      {renderButtons(handleSubmit, reset)}
    </form>
  );
};

export default TaskForm;
